import {
  createBrowserRouter,
  RouterProvider,
  Outlet,
  Navigate,
} from "react-router-dom";
import Panel from "@main/panel-components/Panel";
import Login from "@beeldit/user-and-access/auth/pages/Login";
import { AuthProvider } from "@beeldit/user-and-access/auth/AuthContext";

import { TenantAndCommerceProvider } from "./contexts/TenantAndCommerceContext";

import { ConfigurationsProvider } from "./contexts/ConfigurationsContext";

import { User } from "@beeldit/user-and-access/users/models/User";
import { Commerce } from "./commerces/models/Commerce";

import { EditActionProvider as UserEditActionProvider } from "@beeldit/user-and-access/users/actions/edit/EditActionContext";
import { ListActionProvider as UserListActionProvider } from "@beeldit/user-and-access/users/actions/list/ListActionContext";
import { CreateActionProvider as UserCreateActionProvider } from "@beeldit/user-and-access/users/actions/create/CreateActionContext";
import { RemoveActionProvider as UserRemoveActionProvider } from "@beeldit/user-and-access/users/actions/remove/RemoveActionContext";
import UserPage from "@beeldit/user-and-access/users/pages/UserPage";
import PaymentMethodsPage from "./payment-methods/pages/PaymentMethodsPage";
import RoomsPage from "./rooms/pages/RoomsPage";
import FeesPage from "./fees/pages/FeesPage";
import ExtrasPage from "./extras/pages/ExtrasPage";
import HolidaysPage from "./holidays/pages/HolidaysPage";
import CustomerPage from "./customers/pages/CustomerPage";
import PetPage from "./pets/pages/PetPage";
import CustomersAndPetsPage from "./customers/pages/CustomersAndPetsPage";
import BookingPage from "./bookings/pages/BookingPage";
import BookingsPage from "./bookings/pages/BookingsPage";
import ArrivalBookingsPage from "./bookings/pages/ArrivalBookingsPage";
import DepartureBookingsPage from "./bookings/pages/DepartureBookingsPage";
import BookingsCalendarPage from "./bookings/pages/BookingsCalendarPage";
import PaymentsPage from "./payments/pages/PaymentsPage";

import { ListActionProvider as UsersActivityListActionProvider } from "@beeldit/user-and-access/activity/actions/list/ListActionContext";
import { DetailsActionProvider as UsersActivityDetailsActionProvider } from "@beeldit/user-and-access/activity/actions/details/DetailsActionContext";
import UsersActivityPage from "@beeldit/user-and-access/activity/pages/UsersActivityPage";
import ConfigurationPage from "./configurations/pages/ConfigurationPage";
import UpdatesPage from "./updates/pages/UpdatesPage";
import IntegrationsPage from "./integrations/pages/IntegrationsPage";
import CustomReportsPage from "./custom-reports/pages/CustomReportsPage";
import CustomReportPage from "./custom-reports/pages/CustomReportPage";
import BDConnectionsPage from "./bd-connections/pages/BDConnectionsPage";
import InvoiceRouterWrapper from "./modules/bloonde/billing/invoices/wrappers/InvoiceRouterWrapper";
import DeliveryNoteRouterWrapper from "./modules/bloonde/billing/delivery-notes/wrappers/DeliveryNoteRouterWrapper";
import CustomerWithModalSelector from "./customers/components/CustomerWithModalSelector";

const redirectFunction = (user: User) => {
  let extraData = user.extra_data;  
  let availableTenants = extraData.tenants;  
  let selectedTenant = availableTenants[0];
  if (!selectedTenant) {
    return "/panel";
  }
  let selectedTenantId = selectedTenant.id;
  let availableCommerces = extraData.commerces;
  let selectedCommerce = availableCommerces.find(
    (commerce: Commerce) => commerce.tenant_id = selectedTenantId
  );
  let selectedCommerceId = selectedCommerce.id;
  return `/panel/${selectedTenantId}/${selectedCommerceId}/events`;
}

const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <AuthProvider>
        <ConfigurationsProvider>
          <div className="App">
            <Outlet />
          </div>
        </ConfigurationsProvider>
      </AuthProvider>
    ),
    children: [
      {
        path: "/login",
        element: <Login redirectFunction={(user: User) => {
          return redirectFunction(user)
        }}/>,
      },
      {
        path: "/",
        index: true,
        element: <Navigate to="/panel/1/1" />,
      },
      {
        path: "/panel",
        index: true,
        element: <Navigate to="/panel/1/1" />,
      },
      {
        path: "/panel/:tenantId/:commerceId",
        element: (
          <TenantAndCommerceProvider>
            <Panel />
          </TenantAndCommerceProvider>
        ),
        children: [
          {
            path: 'configurations',
            children: [
              {
                path: "users",
                element: (
                  <UserListActionProvider>
                    <UserRemoveActionProvider>
                      <UserCreateActionProvider>
                        <UserEditActionProvider>
                          <UserPage />
                        </UserEditActionProvider>
                      </UserCreateActionProvider>
                    </UserRemoveActionProvider>
                  </UserListActionProvider>
                ),
              },
              {
                path: "users-activity",
                element: (
                  <UsersActivityListActionProvider>
                    <UsersActivityDetailsActionProvider>
                      <UsersActivityPage />
                    </UsersActivityDetailsActionProvider>
                  </UsersActivityListActionProvider>
                ),
              },
              {
                path: 'payment-methods',
                element: (
                  <PaymentMethodsPage></PaymentMethodsPage>
                )
              },
              {
                path: 'rooms',
                element: (
                  <RoomsPage></RoomsPage>
                )
              },
              {
                path: 'fees',
                element: (
                  <FeesPage></FeesPage>
                )
              },
              {
                path: "extras",
                element: (
                  <ExtrasPage></ExtrasPage>
                )
              },
              {
                path: "holidays",
                element: (
                  <HolidaysPage></HolidaysPage>
                )
              },
              {
                path: "configuration",
                element: (
                  <ConfigurationPage></ConfigurationPage>
                )
              },
              // {
              //   path: "reports",
              //   element: <ReportRouterWrapper />,
              // },
              {
                path: "reports",
                element: <CustomReportsPage></CustomReportsPage>
              },
              {
                path: "reports/:reportId",
                element: <CustomReportPage></CustomReportPage>
              },
              {
                path: "updates",
                element: <UpdatesPage/>,
              },
              {
                path: "integrations",
                element: <IntegrationsPage/>,
              },
              {
                path: "bd-connections",
                element: <BDConnectionsPage/>,
              },
            ]
          },
          {
            path: "customers",
            element: (
              <CustomerPage></CustomerPage>
            )
          },
          {
            path: "pets",
            element: (
              <PetPage></PetPage>
            )
          },
          {
            path: "customers-and-pets",
            element: (
              <CustomersAndPetsPage></CustomersAndPetsPage>
            )
          },
          {
            path: "bookings",
            element: (
              <BookingsPage></BookingsPage>
            )
          },
          {
            path: "booking",
            element: (
              <BookingPage></BookingPage>
            )
          },
          {
            path: "arrival-bookings",
            element: (
              <ArrivalBookingsPage></ArrivalBookingsPage>
            )
          },
          {
            path: "departure-bookings",
            element: (
              <DepartureBookingsPage></DepartureBookingsPage>
            )
          },
          {
            path: "bookings-calendar",
            element: (
              <BookingsCalendarPage></BookingsCalendarPage>
            )
          },
          {
            path: "payments",
            element: (
              <PaymentsPage></PaymentsPage>
            )
          },
          {
            path: "invoices",
            element: (
              <InvoiceRouterWrapper customFormWidgets={{receiver_accountable_id: CustomerWithModalSelector }}/>
            ),
          },
          {
            path: "delivery-notes",
            element: (
              <DeliveryNoteRouterWrapper customFormWidgets={{receiver_accountable_id: CustomerWithModalSelector }}/>
            ),
          },
        ],        
      },
      {
        path: "/panel",
        element: (
          <TenantAndCommerceProvider>
            <Panel />
          </TenantAndCommerceProvider>
        ),
        children: [
          // {
          //   path: "tenants",
          //   element: (
          //     <TenantListActionProvider>
          //       <TenantRemoveActionProvider>
          //         <TenantCreateActionProvider>
          //           <TenantEditActionProvider>
          //             <TenantsPage />
          //           </TenantEditActionProvider>
          //         </TenantCreateActionProvider>
          //       </TenantRemoveActionProvider>
          //     </TenantListActionProvider>
          //   ),
          // },
        ],
      }
    ],
  },
]);

export function Router() {
  return <RouterProvider router={router} />;
}
